import { Box, Flex, useEventListener } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useState } from 'react';

import { useScreenVersion } from '$hooks/use-screen-version';
import { CONTAINER_MAX_W, CONTAINER_PX } from '$shared/layout';
import { useUIStore } from '$stores/ui';
import { throttle } from '$utils/throttle';

import { Content } from './content';
import { Logo } from './logo';
import { SidebarButtons } from './sidebar-buttons';

export const HEADER_HEIGHT_TOP = '88px';
export const HEADER_HEIGHT_SCROLLED = '60px';
const HERO_MAX_W = '1280px';
const HERO_PX = { base: '24px', md: '48px', lg: '80px' };
const SCROLL_THRESHOLD = 24;
const HERO_EXIT_BUFFER = 160;

export function Header() {
  const { sidebarIsOpen, closeSidebar } = useUIStore();
  const isLargeScreen = useScreenVersion('LG');
  const { pathname } = useRouter();
  const isHome = pathname === '/';

  const [scrolled, setScrolled] = useState(false);
  const [compact, setCompact] = useState(false);

  useEventListener(
    'scroll',
    throttle(() => {
      const y = window.scrollY;
      setScrolled(y > SCROLL_THRESHOLD);
      const compactThreshold = window.innerHeight - HERO_EXIT_BUFFER;
      setCompact(y > compactThreshold);
    }, 80),
  );

  function handleNavigate() {
    closeSidebar();
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  }

  const headerHeight = scrolled ? HEADER_HEIGHT_SCROLLED : HEADER_HEIGHT_TOP;
  const innerPaddingY = scrolled ? '8px' : '16px';
  const spacerHeight = isHome ? '0' : HEADER_HEIGHT_TOP;

  const expanded = isHome && !compact;
  const outerPx = expanded ? HERO_PX : CONTAINER_PX;
  const innerMaxW = expanded ? HERO_MAX_W : CONTAINER_MAX_W;

  const stickyStyles = {
    pos: 'fixed' as const,
    zIndex: 'modal' as const,
    top: '0',
    left: '0',
    w: 'full',
    h: headerHeight,
    bg: scrolled ? 'bg.canvas' : 'transparent',
    borderBottomWidth: '1px',
    borderColor: scrolled ? 'line.subtle' : 'transparent',
    backdropFilter: scrolled ? 'blur(12px) saturate(140%)' : 'none',
    boxShadow: scrolled ? '0 6px 24px -16px rgba(20, 17, 14, 0.18)' : 'none',
    px: outerPx,
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    transition:
      'background-color 0.3s ease, border-color 0.3s ease, height 0.3s ease, backdrop-filter 0.3s ease, box-shadow 0.3s ease, padding 0.3s ease',
  };

  if (!isLargeScreen) {
    return (
      <>
        <Box h={spacerHeight} />

        <Box {...stickyStyles}>
          <Flex
            w="full"
            maxW={innerMaxW}
            mx="auto"
            py={innerPaddingY}
            justify="space-between"
            align="center"
            transition="padding 0.3s ease, max-width 0.3s ease"
          >
            <Box pointerEvents={sidebarIsOpen ? 'none' : 'auto'}>
              <Logo scrolled={scrolled} />
            </Box>
            <SidebarButtons sidebarIsOpen={sidebarIsOpen} />
          </Flex>
        </Box>

        <Flex
          pos="fixed"
          top="0"
          right="0"
          h="100vh"
          w={{ base: '100%', sm: '360px' }}
          bg="bg.canvas"
          borderLeftWidth="1px"
          borderColor="line.subtle"
          zIndex="overlay"
          direction="column"
          justify="space-between"
          p="24px"
          pt={`calc(${HEADER_HEIGHT_TOP} + 8px)`}
          transform={sidebarIsOpen ? 'translateX(0)' : 'translateX(110%)'}
          transition="transform 0.3s ease"
        >
          <Content
            isLargeScreen={false}
            onNavigate={handleNavigate}
            scrolled={scrolled}
            largeNav
          />
        </Flex>
      </>
    );
  }

  return (
    <>
      <Box h={spacerHeight} />

      <Box {...stickyStyles}>
        <Flex
          w="full"
          maxW={innerMaxW}
          mx="auto"
          py={innerPaddingY}
          justify="space-between"
          align="center"
          transition="padding 0.3s ease, max-width 0.3s ease"
        >
          <Content
            isLargeScreen
            onNavigate={handleNavigate}
            scrolled={scrolled}
          />
        </Flex>
      </Box>
    </>
  );
}
